import {ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output} from '@angular/core';
import {MatIcon} from '@angular/material/icon';
import {NgClass} from '@angular/common';
import {ClaimFile, ClaimFileType, PresignedClaimFileUrlResponse} from '../../entity/claim-file';
import {MatProgressBar} from '@angular/material/progress-bar';
import {FileUploadService} from '../../control/file-upload-service';
import {LocalizationPipePipe} from '../../../localization/control/localisation-pipe-pipe';

export interface FileUploadConfig {
  title: string;
  subtitle: string;
  icon: string;
  multiple: boolean;
  acceptedTypes: string[];
  maxSize: string;
  dropzoneText: string;
  optional?: boolean;
  actionName: string
  claimFileType: ClaimFileType
}

export type UploadingStatus = {
  status: 'uploading' | 'completed' | 'error';
}

@Component({
  selector: 'app-claim-file-uploads',
  imports: [
    MatIcon,
    NgClass,
    MatProgressBar,
    LocalizationPipePipe
  ],
  templateUrl: './claim-file-uploads.html',
  standalone: true,
  styleUrl: './claim-file-uploads.scss'
})
export class ClaimFileUploads implements OnInit, OnChanges {
  @Input() presignedUrlSupplier!: ((claimId: string, claimFileType: ClaimFileType, parameters: ClaimFile[]) => Promise<PresignedClaimFileUrlResponse>);
  @Input() config: FileUploadConfig = {
    title: 'Photos/Documents',
    subtitle: 'Please upload photos/deed/layouts/...',
    icon: 'camera_alt',
    multiple: true,
    acceptedTypes: ['JPG', 'PNG', 'PDF', 'DOC', 'DOCX'],
    maxSize: '5MB per file',
    dropzoneText: 'Select multiple files',
    actionName: 'Upload',
    claimFileType: ClaimFileType.Others
  };
  uploading: boolean = false;
  uploaded: boolean = false;
  @Output() filesChange = new EventEmitter<File[]>();
  @Output() filesUploadingChange = new EventEmitter<UploadingStatus>();
  @Output() uploadSkipped = new EventEmitter<boolean>();
  @Output() validityChange = new EventEmitter<boolean>();

  private allowedFileExtensions: string[] = [];
  selectedFiles: File[] = [];
  isDragOver = false;
  protected errorMessages: string[] = [];
  @Input() claimId?: string | undefined;
  protected hasError: boolean = false;


  constructor(
    private readonly fileUploadService: FileUploadService,
    private readonly cdr: ChangeDetectorRef) {
  }

  ngOnInit(): void {
    this.allowedFileExtensions = this.getAcceptAttribute().toLowerCase().split(',')
  }

  ngOnChanges(): void {
    this.allowedFileExtensions = this.getAcceptAttribute().toLowerCase().split(',')
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
    const files = Array.from(event.dataTransfer?.files || []);
    this.handleFiles(files);
  }

  onFileSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    this.handleFiles(files);
  }

  private handleFiles(files: File[]) {
    const errorMessages: string [] = []
    const filteredFiles = files.filter(e => {
      const strings = e.name.split('.');
      const splitElement = '.' + strings[strings.length - 1]?.toLowerCase();
      let b = this.allowedFileExtensions.includes(splitElement);
      if (!b) {
        errorMessages.push(`File "${e.name}" is rejected because it of its extension.`)
      }
      return b;
    });
    if (this.config.multiple) {
      this.selectedFiles.push(...filteredFiles);
    } else {
      this.selectedFiles = filteredFiles.slice(0, 1);
    }

    this.hasError = (files.length > filteredFiles.length);
    if (this.hasError) {
      this.errorMessages = [
        `Allowed extensions: ${this.allowedFileExtensions.join(', ')}.`,
        ...errorMessages
      ];
    } else {
      this.errorMessages = []
    }

    this.emitChanges();
  }

  removeFile(index: number) {
    this.selectedFiles.splice(index, 1);
    this.emitChanges();
  }

  private emitChanges() {
    this.filesChange.emit([...this.selectedFiles]);
    this.validityChange.emit(this.selectedFiles.length > 0);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getAcceptAttribute(): string {
    const extensions = this.config.acceptedTypes.map(type => {
      switch (type.toUpperCase()) {
        case 'JPG':
          return '.jpg,.jpeg';
        default:
          return `.${type.toLowerCase()},.${type.toUpperCase()}`;
      }
    });
    return extensions
      .map(e => e.trim())
      .filter(e => e.length > 0)
      .join(',');
  }

  uploadFiles() {
    this.errorMessages = [];
    this.uploading = true;
    console.log('uploadFiles', this.selectedFiles)
    const files = this.selectedFiles.map(file => {
      const item: ClaimFile = {
        fileName: file.name,
        fileSize: file.size,
        contentType: file.type,
      }
      return item;
    })

    if (this.claimId === undefined) {
      console.error('claimId is undefined')
      return
    }


    const fileCompleteProgress: Record<string, boolean> = {}

    const updateOnCompletion = (r: Response, presignedURL: string, file: File) => {
      fileCompleteProgress[presignedURL] = r.ok;
      if (!r.ok) {
        this.errorMessages = [`Error uploading file: ${file.name}`, ...this.errorMessages];
      }
      this.uploading = !Object.values(fileCompleteProgress).every(value => value === true);
      if (!this.uploading) {
        this.uploaded = true;
        this.cdr.detectChanges()
        this.filesUploadingChange.emit({status: 'completed'})
      }
      this.cdr.detectChanges()
      console.log(
        'fileCompleteProgress',
        fileCompleteProgress,
        Object.values(fileCompleteProgress).every(value => value === true),
        this.uploading
      )
    }

    this.getPresignedUrls(files)
      .then(r => {
          r.files.forEach((value, i) => {
            fileCompleteProgress[value.signedURL] = false;
            this.filesUploadingChange.emit({status: 'uploading'})
            const file = this.selectedFiles[i];
            this.fileUploadService.externalUpload(
              value.signedURL,
              file,
              (r) => updateOnCompletion(r, value.signedURL, file),
            )
          })
        }
      )
      .catch(error => {
        console.error('Error uploading files:', error);
        this.errorMessages = [`Error uploading files`, ...this.errorMessages];
        this.cdr.detectChanges();
      })
      .finally()
  }

  private getPresignedUrls(files: ClaimFile[]): Promise<PresignedClaimFileUrlResponse> {
    return this.presignedUrlSupplier(`${this.claimId}`, this.config.claimFileType, files)
  }

  skipUpload() {
    this.uploadSkipped.emit(true);
  }
}
